import {
  Group, Mesh, BoxGeometry, ConeGeometry, CylinderGeometry,
  MeshLambertMaterial, MeshPhongMaterial, Scene, Vector3,
} from 'three';
import { IEnemy } from './IEnemy';

type AIState = 'patrol' | 'chase' | 'attack';

const PATROL_SPEED    = 180;
const CHASE_SPEED     = 240;
const DETECT_RANGE    = 3000;
const ATTACK_RANGE    = 1500;
const ATTACK_COOLDOWN = 5;
const TURN_RATE       = 1.4;

/**
 * 敵戦闘機モデル（全長約14m）
 */
function buildEnemyModel(aggressive: boolean): Group {
  const root = new Group();

  const mainColor  = aggressive ? 0xaa2222 : 0x8a7a5a;
  const darkColor  = aggressive ? 0x661111 : 0x5a4a3a;
  const matMain    = new MeshLambertMaterial({ color: mainColor });
  const matDark    = new MeshLambertMaterial({ color: darkColor });
  const matBlack   = new MeshLambertMaterial({ color: 0x111118 });
  const matGlass   = new MeshPhongMaterial({ color: 0x334455, emissive: 0x001122, shininess: 80, transparent: true, opacity: 0.7 });
  const matExhaust = new MeshPhongMaterial({ color: 0x222228, emissive: aggressive ? 0xff1100 : 0xff5500, emissiveIntensity: 0.8 });

  // ── 胴体 ─────────────────────────────────────────────
  const body = new Mesh(new BoxGeometry(2.8, 1.0, 12), matMain);
  root.add(body);

  // 機首
  const noseCone = new Mesh(new ConeGeometry(0.6, 3.5, 4), matDark);
  noseCone.rotation.x = Math.PI / 2;
  noseCone.position.set(0, 0, -7.8);
  root.add(noseCone);

  const radome = new Mesh(new ConeGeometry(0.3, 1.0, 8), matBlack);
  radome.rotation.x = Math.PI / 2;
  radome.position.set(0, 0, -9.4);
  root.add(radome);

  // コックピット
  const canopy = new Mesh(new BoxGeometry(1.2, 0.6, 2.0), matGlass);
  canopy.position.set(0, 0.8, -2.5);
  root.add(canopy);
  const cpBase = new Mesh(new BoxGeometry(1.6, 0.18, 2.0), matDark);
  cpBase.position.set(0, 0.5, -2.5);
  root.add(cpBase);

  // 機体後部
  const rear = new Mesh(new BoxGeometry(2.6, 1.1, 3.5), matDark);
  rear.position.set(0, 0, 5.5);
  root.add(rear);

  // ── 主翼（後退翼） ───────────────────────────────────
  for (const sx of [-1, 1]) {
    const wing = new Mesh(new BoxGeometry(4.8, 0.2, 5.0), matMain);
    wing.position.set(sx * 3.7, -0.1, 1.5);
    wing.rotation.z = sx * 0.05;
    root.add(wing);
    const tip = new Mesh(new BoxGeometry(0.5, 0.18, 2.2), matDark);
    tip.position.set(sx * 6.2, -0.1, 2.2);
    root.add(tip);
    const strake = new Mesh(new BoxGeometry(1.2, 0.12, 2.8), matMain);
    strake.position.set(sx * 1.6, -0.05, -1.0);
    root.add(strake);
  }

  // ── 水平尾翼 ────────────────────────────────────────
  for (const sx of [-1, 1]) {
    const htail = new Mesh(new BoxGeometry(2.8, 0.14, 2.0), matDark);
    htail.position.set(sx * 2.2, -0.05, 5.8);
    root.add(htail);
  }

  // ── 垂直尾翼 ─────────────────────────────────────────
  const vtail = new Mesh(new BoxGeometry(0.3, 2.2, 2.8), matDark);
  vtail.position.set(0, 1.3, 4.8);
  root.add(vtail);

  // ── エンジンノズル ────────────────────────────────────
  const nozzle = new Mesh(new CylinderGeometry(0.65, 0.75, 1.6, 10), matExhaust);
  nozzle.rotation.x = Math.PI / 2;
  nozzle.position.set(0, -0.05, 6.8);
  root.add(nozzle);
  const inner = new Mesh(new CylinderGeometry(0.45, 0.45, 0.3, 10), matBlack);
  inner.rotation.x = Math.PI / 2;
  inner.position.set(0, -0.05, 7.65);
  root.add(inner);

  // ── インテーク ────────────────────────────────────────
  for (const sx of [-1, 1]) {
    const intake = new Mesh(new BoxGeometry(0.9, 0.55, 2.2), matBlack);
    intake.position.set(sx * 1.3, -0.22, 0.8);
    root.add(intake);
  }

  // ── ミサイルポッド（翼下） ────────────────────────────
  for (const sx of [-1, 1]) {
    const pod = new Mesh(new CylinderGeometry(0.18, 0.18, 2.0, 6), matDark);
    pod.rotation.x = Math.PI / 2;
    pod.position.set(sx * 3.5, -0.5, 0.5);
    root.add(pod);
    const podNose = new Mesh(new ConeGeometry(0.18, 0.5, 6), matBlack);
    podNose.rotation.x = Math.PI / 2;
    podNose.position.set(sx * 3.5, -0.5, -0.6);
    root.add(podNose);
  }

  return root;
}

export class EnemyFighter implements IEnemy {
  readonly group: Group;
  private hp: number;
  private state: AIState = 'patrol';
  private patrolPoints: Vector3[];
  private patrolIdx = 0;
  private attackTimer = 0;
  isAlive = true;
  hitRadius = 12;
  readonly isGroundUnit = false;

  /** EnemyManagerが設定する弾丸発射コールバック */
  onFireBullet: ((from: Vector3, dir: Vector3) => void) | null = null;

  constructor(
    private scene: Scene,
    pos: [number, number, number],
    private aggressive: boolean = false,
  ) {
    this.hp = 60;
    this.group = buildEnemyModel(aggressive);
    this.group.position.set(...pos);
    scene.add(this.group);

    const [x, y, z] = pos;
    this.patrolPoints = [
      new Vector3(x + 800, y,       z),
      new Vector3(x + 800, y + 120, z + 800),
      new Vector3(x,       y,       z + 800),
      new Vector3(x - 400, y - 60,  z),
    ];
  }

  get position(): Vector3 { return this.group.position; }

  takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.isAlive = false;
      this.scene.remove(this.group);
    }
    return !this.isAlive;
  }

  /** @returns プレイヤーへのダメージ量 */
  update(delta: number, playerPos: Vector3): number {
    if (!this.isAlive) return 0;

    const dist = this.group.position.distanceTo(playerPos);
    this.attackTimer = Math.max(0, this.attackTimer - delta);

    if (dist < DETECT_RANGE || this.aggressive) {
      this.state = dist < ATTACK_RANGE ? 'attack' : 'chase';
    } else {
      this.state = 'patrol';
    }

    let targetPos: Vector3;
    let speed = PATROL_SPEED;

    if (this.state === 'patrol') {
      targetPos = this.patrolPoints[this.patrolIdx];
      if (this.group.position.distanceTo(targetPos) < 120) {
        this.patrolIdx = (this.patrolIdx + 1) % this.patrolPoints.length;
      }
    } else {
      targetPos = playerPos.clone();
      speed = CHASE_SPEED;
    }

    const toTarget = targetPos.clone().sub(this.group.position).normalize();
    const currentFwd = new Vector3(0, 0, -1).applyQuaternion(this.group.quaternion);
    currentFwd.lerp(toTarget, Math.min(TURN_RATE * delta, 1)).normalize();
    this.group.lookAt(this.group.position.clone().add(currentFwd));
    this.group.position.addScaledVector(currentFwd, speed * delta);

    if (this.group.position.y < 80) this.group.position.y = 80;

    // 攻撃: 機銃発射
    if (this.state === 'attack' && this.attackTimer <= 0) {
      this.attackTimer = ATTACK_COOLDOWN;

      if (this.onFireBullet) {
        const fwd = new Vector3(0, 0, -1).applyQuaternion(this.group.quaternion);
        const from = this.group.position.clone().addScaledVector(fwd, 9);
        this.onFireBullet(from, fwd);
      }

    }
    return 0;
  }
}
